import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import i18next from 'i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal, refreshGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import { getActionAuthorization } from '@selectors/commons/utils';
import { getAuthorizedActions } from '@selectors/featureProps';
import { getClientInfo } from '@selectors/client';
import { haveSameIds, mergeReportedSelection } from '@commons/utils/listViewSelection';
import { ListView } from '@commons/utils/styledLibraryComponents';
import DeepsightFiltersButton from '@commons/DeepsightAnalyticsHeaders/components/DeepsightFiltersButton';
import EmptyState from '@commons/EmptyState';
import NavigationBar from '@commons/NavigationBar';

import { externalClient as externalClientService } from '@services/externalClient';
import { product as productService } from '@services/product';

import { exportExternalClientCatalog } from '../utils/export';
import { getCatalogProductColumns } from '../utils/getCatalogProductColumns';
import { getCloneCatalogModal } from '../utils/getCloneCatalogModal';
import { getManualProductModal } from '../utils/getManualProductModal';

import CatalogEmptyState from './components/CatalogEmptyState';

import { getActions, getRowActions } from './utils/getActions';
import { getCatalogFilterList } from './utils/getCatalogFilterList';
import {
  getCloneConfirmationModal,
  getRemoveProductsModal,
} from './utils/getCatalogConfirmationModals';
import { getPriceModal } from './utils/getPriceModal';

import { Content, PageContainer } from './styledComponents';

const EXTERNAL_CLIENTS_PATH = '/external/clients';
const EXTERNAL_CLIENT_EDIT_ACTION = 'external-client:edit';

const DEFAULT_ORDER_BY = 'name';
const DEFAULT_ORDER_TYPE = 'asc';
const DEFAULT_MAX_PER_PAGE = 20;
const FIRST_PAGE = 1;

/**
 * External-client detail page — "Catalogue" tab (/external/clients/:id/catalog). Server-paginated
 * catalog of the client with search (name + SKU) and category / sub-category / central-product
 * filters. Consultation only needs the page to be associated with the user role; every edition
 * (add, remove, price, clone) is gated by the `external-client:edit` right and hidden without it.
 * All the modals go through the shared GenericModal, so each of them is driven by a piece of state
 * and re-issued on change to keep its footer in sync with what is selected / typed.
 */
const ExternalClientCatalog = (props) => {
  const {
    match: { path, params },
    stores,
    storeName,
    hasCentralKitchens,
    currency,
    canEdit,
    isGenericModalOpen,
    openModal,
    refreshModal,
    displaySuccessMessage,
    displayErrorMessage,
    pageLoading,
    pageLoaded,
  } = props;

  const listViewRef = useRef(null);
  const [columns] = useState(getCatalogProductColumns(hasCentralKitchens, { withVatRate: true }));

  const [externalClient, setExternalClient] = useState(null);

  const [catalogProducts, setCatalogProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isCatalogEmpty, setIsCatalogEmpty] = useState(false);
  // Until the first fetch resolves the page cannot tell the empty catalog view from the list one,
  // so it renders nothing under the global dark loader instead of flashing the wrong view.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [refreshIndex, setRefreshIndex] = useState(0);

  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [orderType, setOrderType] = useState(DEFAULT_ORDER_TYPE);
  const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
  const [maxPerPage, setMaxPerPage] = useState(DEFAULT_MAX_PER_PAGE);

  const [areFiltersReady, setAreFiltersReady] = useState(false);
  const [columnsFilterList, setColumnsFilterList] = useState([]);
  const [filters, setFilters] = useState(null);
  const [advancedFilters, setAdvancedFilters] = useState(null);
  const [applyFilters, setApplyFilters] = useState(false);
  // Conditions sent in the catalog request body, keyed by the API filter names.
  const [catalogFilters, setCatalogFilters] = useState({});

  const [selectedProducts, setSelectedProducts] = useState([]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [productIdsToAdd, setProductIdsToAdd] = useState([]);

  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [cloneSourceClientId, setCloneSourceClientId] = useState(null);
  const [isCloneConfirmationOpen, setIsCloneConfirmationOpen] = useState(false);

  const [productToEditPrice, setProductToEditPrice] = useState(null);
  const [priceToApply, setPriceToApply] = useState(null);

  const [productIdsToRemove, setProductIdsToRemove] = useState([]);
  const [isSingleProductRemoval, setIsSingleProductRemoval] = useState(false);

  const selectedProductIds = useMemo(
    () => selectedProducts.map(({ id }) => id),
    [selectedProducts],
  );

  const associatedStores = useMemo(() => {
    const clientStoreIds = (externalClient?.stores || []).map(({ id }) => id);

    return stores.filter(({ id }) => clientStoreIds.includes(id));
  }, [externalClient, stores]);

  const catalogFiltersKey = JSON.stringify(catalogFilters);

  // The empty state stands for "this client has no catalog", so it can only be trusted while
  // nothing narrows the list — a search or a filter returning nothing is a plain "no result".
  const hasActiveNarrowing = !!search || !isEmpty(catalogFilters);

  // The dark loader covers the whole first load; it is cleared when the first catalog fetch
  // resolves, or on unmount when the user leaves the page before that.
  useEffect(() => {
    pageLoading();

    return () => {
      pageLoaded();
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const fetchedClient = await externalClientService.getExternalClient(params.id);

        setExternalClient(fetchedClient);
      } catch {
        displayErrorMessage(i18next.t('EXTERNAL_CLIENTS.DETAILS.FETCH_ERROR'));
      }
    })();
  }, [params.id]);

  useEffect(() => {
    (async () => {
      try {
        const { categories, subCategories } = await productService.getProductsCategories();

        setColumnsFilterList(
          getCatalogFilterList(categories || [], subCategories || [], hasCentralKitchens),
        );
      } catch {
        setColumnsFilterList([]);
      } finally {
        setAreFiltersReady(true);
      }
    })();
  }, [hasCentralKitchens]);

  /**
   * A superseded or abandoned fetch must not touch the state nor the global loader: settling after
   * an unmount would otherwise dispatch a stale `pageLoaded` and kill the loader of the next page.
   */
  const fetchCatalog = async (isStillCurrent) => {
    setIsLoading(true);

    try {
      const { catalog, totalCount: count } = await externalClientService.getExternalClientCatalog(
        params.id,
        { page: currentPage, limit: maxPerPage, search: search || '', orderBy, orderType },
        catalogFilters,
      );

      if (!isStillCurrent()) {
        return;
      }

      // ListView keys its rows on `id` while the catalog is keyed on the product it points to.
      setCatalogProducts((catalog || []).map((row) => ({ ...row, id: row.productId })));
      setTotalCount(count || 0);

      if (!hasActiveNarrowing) {
        setIsCatalogEmpty(!count);
      }
    } catch {
      if (!isStillCurrent()) {
        return;
      }

      setCatalogProducts([]);
      setTotalCount(0);

      displayErrorMessage(i18next.t('EXTERNAL_CLIENTS.CATALOG.FETCH_ERROR'));
    } finally {
      if (isStillCurrent()) {
        setIsLoading(false);

        if (!hasLoadedOnce) {
          setHasLoadedOnce(true);
          pageLoaded();
        }
      }
    }
  };

  // Wait for the filter options so the very first fetch already carries the applied conditions.
  useEffect(() => {
    if (!areFiltersReady) {
      return;
    }

    let isCurrentQuery = true;

    fetchCatalog(() => isCurrentQuery);

    return () => {
      isCurrentQuery = false;
    };
  }, [
    areFiltersReady,
    search,
    orderBy,
    orderType,
    currentPage,
    maxPerPage,
    catalogFiltersKey,
    refreshIndex,
  ]);

  const backToFirstPage = () => {
    if (listViewRef.current) {
      listViewRef.current.resetPagination();
    }

    setCurrentPage(FIRST_PAGE);
  };

  /**
   * Turn the applied advanced filters into the request body conditions. Each filter carries the
   * operation builder of its type, which maps its selection to the drizzle descriptor the catalog
   * route expects (e.g. `{ inArray: [...] }`); the modal only keeps the displayed, valued filters.
   */
  useEffect(() => {
    if (!applyFilters) {
      return;
    }

    setApplyFilters(false);
    backToFirstPage();

    if (isEmpty(advancedFilters)) {
      setCatalogFilters({});

      return;
    }

    const appliedFilters = advancedFilters.reduce(
      (result, { getQueryParamOperation, propertyKey, value }) => {
        if (!getQueryParamOperation) {
          return result;
        }

        result[propertyKey] = getQueryParamOperation(propertyKey, value);

        return result;
      },
      {},
    );

    setCatalogFilters(appliedFilters);
  }, [advancedFilters, applyFilters]);

  /**
   * Refresh after a catalog mutation. The added / removed rows shift the pages around, so the
   * selection is dropped and the list goes back to its first page — both in this state and in the
   * ListView internal one. The refresh index covers the case where page 1 is already displayed,
   * where nothing else in the fetch dependencies would change.
   */
  const refreshCatalog = () => {
    setSelectedProducts([]);
    backToFirstPage();
    setRefreshIndex((currentIndex) => currentIndex + 1);
  };

  const handleSearchInputChange = (value) => {
    setSearch(value);
    setCurrentPage(FIRST_PAGE);
  };

  const handleMaxPerPageChange = (value) => {
    setMaxPerPage(value);
    setCurrentPage(FIRST_PAGE);
  };

  const handleSelection = (selectedItems) => {
    const nextSelectedIds = mergeReportedSelection(
      selectedItems,
      catalogProducts.map(({ id }) => id),
      selectedProductIds,
    );

    if (haveSameIds(nextSelectedIds, selectedProductIds)) {
      return;
    }

    const knownProductsById = new Map(
      [...selectedProducts, ...catalogProducts].map((product) => [product.id, product]),
    );

    setSelectedProducts(nextSelectedIds.map((id) => knownProductsById.get(id)).filter(Boolean));
  };

  const displayModal = (modalConfig) => {
    if (isGenericModalOpen) {
      refreshModal(modalConfig);

      return;
    }

    openModal(modalConfig);
  };

  /***********************/
  /* Catalog mutations   */
  /***********************/

  const resetAddModal = () => {
    setIsAddModalOpen(false);
    setProductIdsToAdd([]);
  };

  const handleConfirmAdd = async () => {
    const productIds = productIdsToAdd;

    resetAddModal();
    pageLoading();

    try {
      await externalClientService.addProductsToCatalogs([params.id], productIds);

      displaySuccessMessage(i18next.t('EXTERNAL_CLIENTS.CATALOG.ADD_SUCCESS'));
      refreshCatalog();
    } catch {
      displayErrorMessage(i18next.t('EXTERNAL_CLIENTS.CATALOG.ADD_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const resetCloneModal = () => {
    setIsCloneModalOpen(false);
    setIsCloneConfirmationOpen(false);
    setCloneSourceClientId(null);
  };

  // The picker only gathers the source client: the clone wipes the current catalog, so the
  // destructive part is confirmed in a second modal opened once the picker is closed.
  const handleConfirmCloneSource = () => {
    setIsCloneModalOpen(false);
    setIsCloneConfirmationOpen(true);
  };

  const handleConfirmClone = async () => {
    const sourceExternalClientId = cloneSourceClientId;

    resetCloneModal();
    pageLoading();

    try {
      await externalClientService.cloneCatalog(params.id, sourceExternalClientId);

      displaySuccessMessage(i18next.t('EXTERNAL_CLIENTS.CATALOG.CLONE_SUCCESS'));
      refreshCatalog();
    } catch {
      displayErrorMessage(i18next.t('EXTERNAL_CLIENTS.CATALOG.CLONE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const resetPriceModal = () => {
    setProductToEditPrice(null);
    setPriceToApply(null);
  };

  const handleConfirmPrice = async () => {
    const productIds = [productToEditPrice.id];
    const priceWithTaxes = parseFloat(priceToApply);

    resetPriceModal();
    pageLoading();

    try {
      await externalClientService.updateCatalogProductsPrice(
        [params.id],
        productIds,
        priceWithTaxes,
      );

      displaySuccessMessage(i18next.t('EXTERNAL_CLIENTS.CATALOG.PRICE_SUCCESS'));
      refreshCatalog();
    } catch {
      displayErrorMessage(i18next.t('EXTERNAL_CLIENTS.CATALOG.PRICE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const resetRemoveModal = () => {
    setProductIdsToRemove([]);
    setIsSingleProductRemoval(false);
  };

  const handleConfirmRemove = async () => {
    const productIds = productIdsToRemove;

    resetRemoveModal();
    pageLoading();

    try {
      await externalClientService.removeProductsFromCatalogs([params.id], productIds);

      displaySuccessMessage(i18next.t('EXTERNAL_CLIENTS.CATALOG.REMOVE_SUCCESS'));
      refreshCatalog();
    } catch {
      displayErrorMessage(i18next.t('EXTERNAL_CLIENTS.CATALOG.REMOVE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  /***********************/
  /* Modal orchestration */
  /***********************/

  useEffect(() => {
    if (!isAddModalOpen) {
      return;
    }

    displayModal(
      getManualProductModal({
        stores: associatedStores,
        externalClientId: params.id,
        hasCentralKitchens,
        selectedProductIds: productIdsToAdd,
        onSelectionChange: setProductIdsToAdd,
        onCancel: resetAddModal,
        onConfirm: handleConfirmAdd,
      }),
    );
  }, [isAddModalOpen, productIdsToAdd, associatedStores]);

  useEffect(() => {
    if (!isCloneModalOpen) {
      return;
    }

    displayModal(
      getCloneCatalogModal({
        stores,
        selectedClientId: cloneSourceClientId,
        excludedClientId: params.id,
        onSelectClient: setCloneSourceClientId,
        onCancel: resetCloneModal,
        onConfirm: handleConfirmCloneSource,
      }),
    );
  }, [isCloneModalOpen, cloneSourceClientId, stores]);

  useEffect(() => {
    if (!isCloneConfirmationOpen) {
      return;
    }

    displayModal(
      getCloneConfirmationModal({
        onCancel: resetCloneModal,
        onConfirm: handleConfirmClone,
      }),
    );
  }, [isCloneConfirmationOpen]);

  useEffect(() => {
    if (!productToEditPrice) {
      return;
    }

    displayModal(
      getPriceModal({
        price: priceToApply,
        onChangePrice: setPriceToApply,
        onCancel: resetPriceModal,
        onConfirm: handleConfirmPrice,
      }),
    );
  }, [productToEditPrice, priceToApply]);

  useEffect(() => {
    if (!productIdsToRemove.length) {
      return;
    }

    displayModal(
      getRemoveProductsModal({
        isSingleProduct: isSingleProductRemoval,
        onCancel: resetRemoveModal,
        onConfirm: handleConfirmRemove,
      }),
    );
  }, [productIdsToRemove]);

  /***********************/
  /* Actions             */
  /***********************/

  const openAddModal = () => {
    setIsAddModalOpen(true);
  };

  const openCloneModal = () => {
    setIsCloneModalOpen(true);
  };

  const handleExport = useCallback(async () => {
    const exportParams = {
      clientName: externalClient?.name ?? '',
      hasCentralKitchens,
      currency,
    };

    if (selectedProducts.length) {
      exportExternalClientCatalog(selectedProducts, exportParams);

      return;
    }

    if (!totalCount) {
      return;
    }

    pageLoading();

    try {
      const { catalog } = await externalClientService.getExternalClientCatalog(
        params.id,
        { page: FIRST_PAGE, limit: totalCount, search: search || '', orderBy, orderType },
        catalogFilters,
      );

      exportExternalClientCatalog(catalog ?? [], exportParams);
    } catch {
      displayErrorMessage(i18next.t('EXTERNAL_CLIENTS.CATALOG.EXPORT_ERROR'));
    } finally {
      pageLoaded();
    }
  }, [
    selectedProducts,
    externalClient,
    currency,
    hasCentralKitchens,
    totalCount,
    search,
    orderBy,
    orderType,
    catalogFiltersKey,
  ]);

  const listActions = useMemo(
    () =>
      getActions({
        canEdit,
        hasSelection: !!selectedProductIds.length,
        onAdd: openAddModal,
        onClone: openCloneModal,
        onExport: handleExport,
        onRemoveSelection: () => {
          setIsSingleProductRemoval(false);
          setProductIdsToRemove(selectedProductIds);
        },
      }),
    [canEdit, selectedProductIds, handleExport],
  );

  const listRowActions = useMemo(() => {
    if (!canEdit) {
      return undefined;
    }

    return getRowActions({
      onEditPrice: (catalogProduct) => {
        setProductToEditPrice(catalogProduct);
        setPriceToApply(catalogProduct.priceWithTaxes);
      },
      onRemove: ({ id }) => {
        setIsSingleProductRemoval(true);
        setProductIdsToRemove([id]);
      },
    });
  }, [canEdit]);

  const listData = useMemo(
    () =>
      catalogProducts.map((catalogProduct) => ({
        ...catalogProduct,
        isRowSelected: selectedProductIds.includes(catalogProduct.id),
      })),
    [catalogProducts, selectedProductIds],
  );

  const renderCatalogList = () => (
    <ListView
      actions={listActions}
      columns={columns}
      countElements={totalCount}
      data={listData}
      defaultCurrentPage={currentPage}
      defaultMaxPerPage={maxPerPage}
      defaultOrderBy={orderBy}
      defaultOrderType={orderType}
      defaultSearchInput={search}
      disableSelection={!canEdit}
      handleCurrentPageChange={setCurrentPage}
      handleMaxPerPageChange={handleMaxPerPageChange}
      handleOrderByChange={setOrderBy}
      handleOrderTypeChange={setOrderType}
      handleSearchInputChange={handleSearchInputChange}
      isLoading={isLoading}
      minActionsInActionsDropdown={1}
      padding={'24px 24px 0px 24px'}
      placeholderShape={i18next.t('GENERAL.SEARCH')}
      ref={listViewRef}
      renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
      renderFilterButton={() => (
        <DeepsightFiltersButton
          advancedFilters={advancedFilters}
          applyFilters={applyFilters}
          columnsFilterList={columnsFilterList}
          filters={filters}
          isLoading={isLoading}
          readOnly={isLoading}
          setAdvancedFilters={setAdvancedFilters}
          setApplyFilters={setApplyFilters}
          setFilters={setFilters}
          textFilterButton={i18next.t('GENERAL.LIST_VIEW_FILTER_BUTTON')}
        />
      )}
      rowActions={listRowActions}
      setSelectedItems={handleSelection}
      hideAllPerPageOption
      onQueryParamsChange={() => true}
    />
  );

  const renderContent = () => {
    if (!hasLoadedOnce) {
      return null;
    }

    if (isCatalogEmpty) {
      return <CatalogEmptyState canEdit={canEdit} onAdd={openAddModal} onClone={openCloneModal} />;
    }

    return renderCatalogList();
  };

  return (
    <>
      <NavigationBar
        externalClient={externalClient}
        externalClientId={params.id}
        path={path}
        storeName={storeName}
      />
      <PageContainer>
        <Content>{renderContent()}</Content>
      </PageContainer>
    </>
  );
};

const mapStateToProps = (state) => {
  const authorizedActions = getAuthorizedActions(
    state.baseReducer.userRights,
    EXTERNAL_CLIENTS_PATH,
  );

  const clientInfo = getClientInfo(state.baseReducer.user);

  return {
    stores: state.baseReducer.activeStores,
    storeName: clientInfo.storeName,
    hasCentralKitchens: clientInfo.hasCentralKitchens,
    currency: state.baseReducer.currency,
    isGenericModalOpen: state.modalReducer.GenericModalBool,
    canEdit:
      state.baseReducer.user.isDeepsight ||
      getActionAuthorization(authorizedActions, EXTERNAL_CLIENT_EDIT_ACTION),
  };
};

const mapDispatchToProps = (dispatch) => ({
  openModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
  displaySuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  displayErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ExternalClientCatalog);
